import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Radio,
  RadioGroup,
  Switch,
  FormLabel,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Fade,
  Grid,
  Modal,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Check as CorrectIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorIcon,
  History as HistoryIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { ThemeContext } from "../context/ThemeContext";
import sanitizeHtml from "sanitize-html";
import katex from "katex";
import SearchIcon from "@mui/icons-material/Search";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CreateQuiz = () => {
  // State for quiz configuration
  const [quizTitle, setQuizTitle] = useState("");
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [maxAttempts, setMaxAttempts] = useState("");
  const [passingPercentage, setPassingPercentage] = useState("");
  const [negativeMarking, setNegativeMarking] = useState(false);
  const [isTimerEnabled, setIsTimerEnabled] = useState(false);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("");
  const [numberOfQuestions, setNumberOfQuestions] = useState("");
  const [selectedOptions, setSelectedOptions] = useState({
    FIB: false,
    TF: false,
    MCQ: false,
    IMAGE: false,
    ALL: false,
  });

  // State for folders and tags
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [tagQuestions, setTagQuestions] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [pendingFolders, setPendingFolders] = useState([]);
  const [pendingTagIds, setPendingTagIds] = useState([]);
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState({});
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageQuestion, setSelectedImageQuestion] = useState(null);

  // State for modals and snackbar
  const [quizCreatedModalOpen, setQuizCreatedModalOpen] = useState(false);
  const [createdQuizId, setCreatedQuizId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const { mode, customColors } = useContext(ThemeContext);
  const userRole = sessionStorage.getItem("userRole");
const isStudent = userRole === "student" || userRole === "paid student";
  const token = sessionStorage.getItem("authToken");
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const fetchController = useRef(null);

  // Fetch folders
const fetchFolders = async () => {
  setIsLoading(true);
  const controller = new AbortController();
  fetchController.current = controller;
  try {
    // Always fetch the summary (has question counts)
    const summaryResponse = await fetch(`${apiUrl}/questions/summary/`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      signal: controller.signal,
    });
    if (!summaryResponse.ok) throw new Error(`HTTP error! status: ${summaryResponse.status}`);
    const summaryData = await summaryResponse.json();

    const processFolder = (folder) => ({
      id: folder.folder_id,
      name: folder.folder_name,
      tf: folder.truefalse || 0,
      mcq: folder.mcq || 0,
      fib: folder.fib || 0,
      image: folder.image || 0,
      all: folder.total || 0,
      subfolders: folder.subfolders ? folder.subfolders.map(processFolder) : [],
      questionCounts: {
        mcq: folder.mcq || 0,
        fib: folder.fib || 0,
        tf: folder.truefalse || 0,
        image: folder.image || 0,
        all: folder.total || 0,
      },
    });

    let rawFolders;

    if (isStudent) {
      // Fetch /folders/ to get the allowed folder IDs for this student
      const foldersResponse = await fetch(`${apiUrl}/folders/`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        signal: controller.signal,
      });
      if (!foldersResponse.ok) throw new Error(`HTTP error! status: ${foldersResponse.status}`);
      const foldersData = await foldersResponse.json();

      // Collect only own_folder IDs (parent === null to get top-level only)
      const allowedIds = new Set(
        (foldersData.own_folders || [])
          .filter((f) => f.parent === null)
          .map((f) => f.id)
      );

      // Filter summary by_folder to only allowed IDs
      rawFolders = (summaryData.by_folder || [])
        .filter((f) => allowedIds.has(f.folder_id))
        .map(processFolder);
    } else {
      rawFolders = (summaryData.by_folder || []).map(processFolder);
    }

    const flattenFolders = (list) => {
      const result = [];
      list.forEach((folder) => {
        result.push(folder);
        if (folder.subfolders?.length > 0)
          result.push(...flattenFolders(folder.subfolders));
      });
      return result;
    };

    setFolders(flattenFolders(rawFolders));
  } catch (error) {
    if (error.name !== "AbortError") console.error("Error fetching folders:", error);
  } finally {
    setIsLoading(false);
    fetchController.current = null;
  }
};

  // Fetch available tags
  const fetchAvailableTags = async (folderIds) => {
    if (!folderIds.length) {
      setTags([]);
      setTagIds([]);
      return;
    }
    setIsTagsLoading(true);
    try {
      const response = await fetch(`${apiUrl}/tags/available/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ folder_ids: folderIds }),
      });
      const data = await response.json();
      setTags(data);
    } catch (err) {
      console.error("Error fetching tags:", err);
    } finally {
      setIsTagsLoading(false);
    }
  };

  // Fetch questions by tags
  const fetchQuestionsByTags = async () => {
    const selectedTypes = Object.keys(selectedOptions).filter((k) => selectedOptions[k] && k !== "ALL");
    const types = selectedOptions.ALL
      ? ["MCQ", "FIB", "TRUEFALSE", "IMAGE"]
      : selectedTypes.map((t) => (t === "TF" ? "TRUEFALSE" : t));

    if (!selectedFolders.length || !tagIds.length || !types.length) {
      setTagQuestions({});
      return;
    }

    setIsQuestionsLoading(true);
    try {
      const allFolderIds = getAllFolderIds(selectedFolders, folders);
      const response = await fetch(`${apiUrl}/questions/by-tags/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          folder_ids: allFolderIds,
          tag_ids: tagIds,
          question_types: types,
        }),
      });
      const data = await response.json();
      setTagQuestions(data);
    } catch (err) {
      console.error("Error fetching questions:", err);
    } finally {
      setIsQuestionsLoading(false);
    }
  };

  // Helper functions
  const getAllFolderIds = (selectedFolderIds, allFolders) => {
    const allIds = new Set();
    const processFolder = (folder) => {
      if (selectedFolderIds.includes(folder.id)) {
        allIds.add(folder.id);
        folder.subfolders?.forEach((subfolder) => {
          allIds.add(subfolder.id);
          if (subfolder.subfolders?.length > 0) {
            subfolder.subfolders.forEach(processFolder);
          }
        });
      }
      folder.subfolders?.forEach(processFolder);
    };
    allFolders.forEach(processFolder);
    return Array.from(allIds);
  };

  const getAllSubfolderIds = (folder) => {
    const ids = [];
    const collect = (f) => {
      f.subfolders?.forEach((sf) => {
        ids.push(sf.id);
        collect(sf);
      });
    };
    collect(folder);
    return ids;
  };

  const renderFolderItems = (folderList, depth = 0) => {
    return folderList
      .filter((f) =>
        f.name.toLowerCase().includes(folderSearch.toLowerCase()) ||
        (f.subfolders?.length > 0 && f.subfolders.some(sf => sf.name.toLowerCase().includes(folderSearch.toLowerCase())))
      )
      .map((folder) => (
        <React.Fragment key={folder.id}>
          <MenuItem
            value={folder.id}
            onClick={(e) => {
              e.stopPropagation();
              const isSelected = pendingFolders.includes(folder.id);
              if (isSelected) {
                const idsToRemove = getAllFolderIds([folder.id], [folder]);
                idsToRemove.push(folder.id);
                setPendingFolders((prev) => prev.filter((id) => !idsToRemove.includes(id)));
              } else {
                const idsToAdd = getAllSubfolderIds(folder);
                setPendingFolders((prev) => [...new Set([...prev, folder.id, ...idsToAdd])]);
              }
            }}
            sx={{
              py: 0.75, px: 2, mx: 0.75, borderRadius: "10px", mb: 0.25,
              pl: `${(depth * 20) + 16}px`,
              transition: "all 0.15s ease",
              "&:hover": { backgroundColor: mode === "dark" ? "rgba(30,139,175,0.12)" : "rgba(30,139,175,0.06)" },
              "&.Mui-selected": { backgroundColor: mode === "dark" ? "rgba(30,139,175,0.18)" : "rgba(30,139,175,0.08)" },
            }}
          >
            <Box sx={{
              width: 18, height: 18, borderRadius: "5px",
              border: pendingFolders.includes(folder.id) ? "2px solid #1e8baf" : mode === "dark" ? "2px solid rgba(255,255,255,0.2)" : "2px solid rgba(30,58,138,0.2)",
              background: pendingFolders.includes(folder.id) ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center", mr: 1.5, flexShrink: 0, transition: "all 0.15s ease", pointerEvents: "none"
            }}>
              {pendingFolders.includes(folder.id) && (
                <Box component="svg" viewBox="0 0 12 12" sx={{ width: 10, height: 10 }}>
                  <polyline points="2,6 5,9 10,3" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {depth > 0 && (
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: depth === 1 ? "#1e8baf" : "#94a3b8", flexShrink: 0 }} />
                )}
                <Typography sx={{
                  fontSize: depth === 0 ? "0.875rem" : "0.82rem",
                  fontWeight: pendingFolders.includes(folder.id) ? 600 : depth === 0 ? 600 : 400,
                  color: mode === "dark" ? "#e0e0e0" : pendingFolders.includes(folder.id) ? "#1e3a8a" : depth === 0 ? "#1e3a8a" : "#475569",
                  lineHeight: 1.3,
                }}>
                  {folder.name}
                </Typography>
                <Chip
                  label={depth === 0 ? "Folder" : "Subfolder"}
                  size="small"
                  sx={{
                    height: 16, fontSize: "0.6rem", fontWeight: 700,
                    bgcolor: depth === 0 ? "rgba(30,58,138,0.1)" : "rgba(30,139,175,0.1)",
                    color: depth === 0 ? "#1e3a8a" : "#1e8baf",
                    border: `1px solid ${depth === 0 ? "rgba(30,58,138,0.2)" : "rgba(30,139,175,0.2)"}`,
                    "& .MuiChip-label": { px: 0.75 }
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", mt: 0.2 }}>
                {folder.all} questions
                {folder.subfolders?.length > 0 && ` · ${folder.subfolders.length} subfolder${folder.subfolders.length > 1 ? "s" : ""}`}
              </Typography>
            </Box>
            {pendingFolders.includes(folder.id) && (
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "linear-gradient(135deg, #1e3a8a, #1e8baf)", ml: 1, flexShrink: 0, pointerEvents: "none" }} />
            )}
          </MenuItem>
          {folder.subfolders?.length > 0 && renderFolderItems(folder.subfolders, depth + 1)}
        </React.Fragment>
      ));
  };

  const getTotalAvailableQuestions = () => {
    if (tagIds.length > 0) {
      return Object.values(tagQuestions).reduce((sum, questions) => sum + questions.length, 0);
    }
    const mappedTypes = getMappedTypes();
    return selectedFolders.reduce((total, folderId) => {
      const folder = folders.find((f) => f.id === folderId);
      if (!folder) return total;
      if (mappedTypes.length === 4) return total + (folder.questionCounts?.all || 0);
      return total + mappedTypes.reduce((acc, type) => {
        const key = type === "TRUEFALSE" ? "tf" : type.toLowerCase();
        return acc + (folder.questionCounts?.[key] || 0);
      }, 0);
    }, 0);
  };

  const getMappedTypes = () => {
    const selectedTypes = Object.keys(selectedOptions).filter((k) => selectedOptions[k]);
    if (selectedTypes.includes("ALL")) return ["FIB", "TRUEFALSE", "MCQ", "IMAGE"];
    return selectedTypes.map((type) => {
      switch (type) {
        case "TF": return "TRUEFALSE";
        case "FIB": return "FIB";
        case "MCQ": return "MCQ";
        case "IMAGE": return "IMAGE";
        default: return type;
      }
    });
  };

  const handleCheckboxChange = (option) => {
    setSelectedOptions((prev) => {
      if (option === "ALL") {
        return { FIB: false, TF: false, MCQ: false, IMAGE: false, ALL: !prev.ALL };
      }
      return { ...prev, [option]: !prev[option], ALL: false };
    });
  };

  const renderContent = (html) => {
    if (!html) return null;
    const processedHtml = html.replace(/\[BLANK\]/g, "_________");
    const parts = processedHtml.split(/(\$\$.+?\$\$|\$.+?\$)/g);
    return (
      <span>
        {parts.map((part, index) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2);
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: true });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1);
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: false });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }
          try {
            const sanitizedContent = sanitizeHtml(part, {
              allowedTags: ["b", "i", "em", "strong", "u", "sub", "sup", "s", "blockquote", "code", "ul", "ol", "li", "span"],
              allowedAttributes: { span: ["style"] },
              allowedStyles: { "*": { color: [/.*/], "background-color": [/.*/] } },
              transformTags: { p: "", br: "", div: "span" },
            });
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        })}
      </span>
    );
  };

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return "N/A";
    const num = Number(percentage);
    if (Number.isInteger(num)) return `${num}%`;
    return `${num.toFixed(num % 1 === 0 ? 0 : num < 10 ? 2 : 1)}%`;
  };

  const handleStartQuiz = async () => {
    if (!quizTitle.trim()) {
      setSnackbar({ open: true, message: "Please enter a quiz title.", severity: "error" });
      return;
    }
    if (selectedFolders.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one folder.", severity: "error" });
      return;
    }
    const selectedTypes = Object.keys(selectedOptions).filter((k) => selectedOptions[k]);
    if (selectedTypes.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one question type.", severity: "error" });
      return;
    }
    if (tagIds.length > 0 && selectedQuestions.length === 0) {
      setSnackbar({ open: true, message: "You've selected tags — please select at least one question.", severity: "error" });
      return;
    }

    const mappedTypes = getMappedTypes();
    const autoCalc = getTotalAvailableQuestions();
    const finalNumberOfQuestions = selectedQuestions.length > 0
      ? selectedQuestions.length
      : numberOfQuestions !== "" ? Number(numberOfQuestions) : autoCalc;

    if (finalNumberOfQuestions < 1) {
      setSnackbar({ open: true, message: "Number of questions must be at least 1.", severity: "error" });
      return;
    }

    const payload = {
      title: quizTitle.trim(),
      folders: selectedFolders,
      question_types: mappedTypes,
      number_of_questions: finalNumberOfQuestions,
      passing_percentage: passingPercentage ? Number(passingPercentage) : 40,
      max_attempts: maxAttempts ? Number(maxAttempts) : null,
      neg_marking: negativeMarking,
      time_limit_minutes: isTimerEnabled && timeLimitMinutes ? Number(timeLimitMinutes) : null,
    };

    if (tagIds.length > 0) {
      payload.tag_ids = tagIds;
      payload.selected_questions = selectedQuestions;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrl}/quiz/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCreatedQuizId(data.id);
      setQuizCreatedModalOpen(true);
    } catch (error) {
      console.error("Error starting quiz:", error);
      setSnackbar({ open: true, message: error.message || "Failed to start quiz. Please try again.", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleScheduleQuiz = async () => {
    try {
      const response = await fetch(`${apiUrl}/quiz-schedules/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          quiz: createdQuizId,
          start_time: new Date().toISOString(),
          end_time: new Date(Date.now() + 86400000).toISOString(),
        }),
      });
      if (!response.ok) throw new Error("Failed to schedule quiz");
      const data = await response.json();
      setSnackbar({ open: true, message: `Quiz scheduled successfully! Schedule ID: ${data.id}`, severity: "success" });
      setQuizCreatedModalOpen(false);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    }
  };

  const handleStartNow = () => {
    navigate(`/practice/${createdQuizId}`, {
      state: {
        quizId: createdQuizId,
        questionTypes: getMappedTypes(),
        negativeMarking: negativeMarking,
        timerEnabled: isTimerEnabled && !!timeLimitMinutes,
      }
    });
  };

  useEffect(() => {
    if (token && apiUrl) {
      fetchFolders();
    }
    return () => { if (fetchController.current) fetchController.current.abort(); };
  }, [token, apiUrl]);

  useEffect(() => {
    const allFolderIds = getAllFolderIds(selectedFolders, folders);
    fetchAvailableTags(allFolderIds);
    setPendingTagIds([]);
    setTagIds([]);
    setTagQuestions({});
    setSelectedQuestions([]);
  }, [selectedFolders, folders]);

  useEffect(() => {
    fetchQuestionsByTags();
  }, [selectedFolders, tagIds, selectedOptions]);

  useEffect(() => {
    if (selectedQuestions.length > 0) {
      setNumberOfQuestions(selectedQuestions.length);
    }
  }, [selectedQuestions]);

  return (
    <Box sx={{ padding: 4, backgroundColor: mode === "dark" ? "#121212" : "#F5F5F5", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 4 }}>
        <Paper
          sx={{
            p: 4,
            maxWidth: 1200,
            width: "100%",
            mx: "auto",
            borderRadius: "24px",
            background: mode === "dark" ? "#1E1B18" : mode === "custom" ? customColors.bg : "linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(241,248,255,0.97) 100%)",
            border: "1px solid rgba(30, 139, 175, 0.12)",
            boxShadow: "0 4px 24px rgba(30,58,138,0.08), 0 1px 4px rgba(30,58,138,0.04)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", mb: 4, gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: "0.75rem", color: mode === "dark" ? "#90caf9" : "#64748b", letterSpacing: "0.04em", mt: 0.3 }}>
                Configure and start the quiz
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth label="Quiz Title" value={quizTitle}
            onChange={(e) => setQuizTitle(e.target.value)}
            placeholder="Enter a title for your quiz" required
            helperText="Required - Give your quiz a descriptive title"
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                "& fieldset": { borderColor: "rgba(30,58,138,0.15)" },
                "&:hover fieldset": { borderColor: "rgba(30,139,175,0.4)" },
                "&.Mui-focused fieldset": { borderColor: "#1e8baf", borderWidth: "1.5px" }
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#1e8baf" },
              "& .MuiFormHelperText-root": { fontSize: "0.75rem", color: "#94a3b8" },
            }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ "&.Mui-focused": { color: "#1e8baf" }, fontSize: "0.875rem", fontWeight: 500 }}>Select Folders</InputLabel>
            <Select
              open={folderDropdownOpen}
              onOpen={() => { setFolderDropdownOpen(true); setFolderSearch(""); }}
              onClose={() => setFolderDropdownOpen(false)}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>Select folders...</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const folder = folders.find((f) => f.id === id);
                      return (
                        <Chip key={id} label={folder?.name || "Unknown"} size="small"
                          sx={{
                            height: 22, fontSize: "0.72rem", fontWeight: 600,
                            bgcolor: mode === "dark" ? "rgba(30,139,175,0.25)" : "rgba(30,139,175,0.1)",
                            color: "#1e8baf", border: "1px solid rgba(30,139,175,0.35)",
                            "& .MuiChip-label": { px: 1 }
                          }}
                        />
                      );
                    })}
                  </Box>
                )
              }
              multiple
              value={pendingFolders}
             onChange={() => {}}
              label="Select Folders"
              sx={{
                borderRadius: "12px",
                background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(30,58,138,0.15)", transition: "border-color 0.2s ease" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(30,139,175,0.45)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e8baf", borderWidth: "1.5px" },
                "& .MuiSelect-select": { minHeight: "48px !important", display: "flex", alignItems: "center", py: 1 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    boxShadow: mode === "dark" ? "0 12px 40px rgba(0,0,0,0.5)" : "0 12px 40px rgba(30,58,138,0.12)",
                    mt: 0.75,
                    border: mode === "dark" ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,58,138,0.08)",
                    background: mode === "dark" ? "#1e1b18" : "#ffffff",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "400px",
                  },
                },
                MenuListProps: {
                  sx: {
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                    height: "100%",
                  },
                },
                autoFocus: false,
              }}
            >
              <Box
                sx={{
                  px: 2, pt: 2, pb: 1.5,
                  background: mode === "dark" ? "linear-gradient(180deg, #2a2725 0%, #1e1b18 100%)" : "linear-gradient(180deg, #f8faff 0%, #ffffff 100%)",
                  borderBottom: mode === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(30,58,138,0.06)",
                  position: "sticky", top: 0, zIndex: 10,
                }}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: mode === "dark" ? "#90caf9" : "#1e3a8a", opacity: 0.7, mb: 1 }}>
                  Folders ({folders.length})
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, borderRadius: "8px", border: mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(30,58,138,0.12)", background: mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(30,58,138,0.03)" }}>
                  <SearchIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
                  <Box
                    component="input"
                    value={folderSearch}
                    onChange={(e) => setFolderSearch(e.target.value)}
                    placeholder="Search folders..."
                    autoFocus
                    sx={{
                      border: "none", outline: "none", background: "transparent",
                      fontSize: "0.82rem", color: mode === "dark" ? "#e0e0e0" : "#334155", width: "100%",
                      "&::placeholder": { color: "#94a3b8" }
                    }}
                  />
                  {folderSearch && (
                    <Box component="span" onClick={() => setFolderSearch("")} sx={{ cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1, "&:hover": { color: "#64748b" } }}>✕</Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", py: 0.75, "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "rgba(30,139,175,0.2)", borderRadius: "4px" } }}>
                {folders.filter((f) => f.name.toLowerCase().includes(folderSearch.toLowerCase())).length === 0 ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8" }}>No folders match "{folderSearch}"</Typography>
                  </Box>
                ) : (
                  renderFolderItems(
                    folders.filter((f) => {
                      const allSubIds = new Set(
                        folders.flatMap((folder) => (folder.subfolders || []).map((sf) => sf.id))
                      );
                      return !allSubIds.has(f.id);
                    })
                  )
                )}
              </Box>

              <Box
                sx={{
                  px: 2, py: 1.5,
                  borderTop: mode === "dark" ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,58,138,0.08)",
                  background: mode === "dark" ? "#1e1b18" : "#ffffff",
                  position: "sticky", bottom: 0, zIndex: 10,
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Button size="small"
                    onClick={(e) => { e.stopPropagation(); setPendingFolders(folders.map((f) => f.id)); }}
                    disabled={pendingFolders.length === folders.length}
                    sx={{
                      textTransform: "none", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
                      px: 1.5, py: 0.5, color: "#1e8baf", border: "1px solid rgba(30,139,175,0.3)",
                      "&:hover": { borderColor: "#1e8baf", backgroundColor: "rgba(30,139,175,0.06)" },
                      "&.Mui-disabled": { opacity: 0.4 }
                    }}
                  >
                    All
                  </Button>
                  <Button size="small"
                    onClick={(e) => { e.stopPropagation(); setPendingFolders([]); }}
                    disabled={pendingFolders.length === 0}
                    sx={{
                      textTransform: "none", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
                      px: 1.5, py: 0.5, color: "#64748b", border: "1px solid rgba(100,116,139,0.25)",
                      "&:hover": { borderColor: "#94a3b8", backgroundColor: "rgba(100,116,139,0.06)" },
                      "&.Mui-disabled": { opacity: 0.4 }
                    }}
                  >
                    Clear
                  </Button>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {pendingFolders.length > 0 && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#1e8baf", fontWeight: 600 }}>
                      {pendingFolders.length} selected
                    </Typography>
                  )}
                  <Button size="small" variant="contained"
                    onClick={(e) => { e.stopPropagation(); setSelectedFolders(pendingFolders); setFolderDropdownOpen(false); }}
                    sx={{
                      textTransform: "none", borderRadius: "8px", fontSize: "0.78rem", fontWeight: 700,
                      px: 2.5, py: 0.65,
                      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                      boxShadow: "0 2px 8px rgba(30,58,138,0.25)",
                      "&:hover": { background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)", boxShadow: "0 4px 12px rgba(30,58,138,0.35)" }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ "&.Mui-focused": { color: "#1e8baf" }, fontSize: "0.875rem", fontWeight: 500 }}>Select Tags</InputLabel>
            <Select
              open={tagDropdownOpen}
              onOpen={() => { setTagDropdownOpen(true); setTagSearch(""); }}
              onClose={() => setTagDropdownOpen(false)}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>Select tags...</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const tag = tags.find((t) => t.id === id);
                      return (
                        <Chip key={id} label={tag?.name || "Unknown"} size="small"
                          sx={{
                            height: 22, fontSize: "0.72rem", fontWeight: 600,
                            bgcolor: mode === "dark" ? "rgba(30,139,175,0.25)" : "rgba(30,139,175,0.1)",
                            color: "#1e8baf", border: "1px solid rgba(30,139,175,0.35)",
                            "& .MuiChip-label": { px: 1 }
                          }}
                        />
                      );
                    })}
                  </Box>
                )
              }
              multiple
              value={pendingTagIds}
              onChange={() => {}}
              disabled={!selectedFolders.length || isTagsLoading}
              label="Select Tags"
              sx={{
                borderRadius: "12px",
                background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(30,58,138,0.15)", transition: "border-color 0.2s ease" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(30,139,175,0.45)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e8baf", borderWidth: "1.5px" },
                "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(30,58,138,0.08)" },
                "& .MuiSelect-select": { minHeight: "48px !important", display: "flex", alignItems: "center", py: 1 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    boxShadow: mode === "dark" ? "0 12px 40px rgba(0,0,0,0.5)" : "0 12px 40px rgba(30,58,138,0.12)",
                    mt: 0.75,
                    border: mode === "dark" ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,58,138,0.08)",
                    background: mode === "dark" ? "#1e1b18" : "#ffffff",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "400px",
                  },
                },
                MenuListProps: {
                  sx: {
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                    height: "100%",
                  },
                },
                autoFocus: false,
              }}
            >
              <Box
                sx={{
                  px: 2, pt: 2, pb: 1.5,
                  background: mode === "dark" ? "linear-gradient(180deg, #2a2725 0%, #1e1b18 100%)" : "linear-gradient(180deg, #f8faff 0%, #ffffff 100%)",
                  borderBottom: mode === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(30,58,138,0.06)",
                  position: "sticky", top: 0, zIndex: 10,
                }}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: mode === "dark" ? "#90caf9" : "#1e3a8a", opacity: 0.7, mb: 1 }}>
                  Tags ({tags.length})
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, borderRadius: "8px", border: mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(30,58,138,0.12)", background: mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(30,58,138,0.03)" }}>
                  <SearchIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
                  <Box
                    component="input"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search tags..."
                    autoFocus
                    sx={{
                      border: "none", outline: "none", background: "transparent",
                      fontSize: "0.82rem", color: mode === "dark" ? "#e0e0e0" : "#334155", width: "100%",
                      "&::placeholder": { color: "#94a3b8" }
                    }}
                  />
                  {tagSearch && (
                    <Box component="span" onClick={() => setTagSearch("")} sx={{ cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1, "&:hover": { color: "#64748b" } }}>✕</Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", py: 0.75, "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "rgba(30,139,175,0.2)", borderRadius: "4px" } }}>
                {isTagsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4, gap: 1.5 }}>
                    <CircularProgress size={18} sx={{ color: "#1e8baf" }} />
                    <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8" }}>Loading tags...</Typography>
                  </Box>
                ) : tags.filter((t) => t.name.toLowerCase().includes(tagSearch.toLowerCase())).length === 0 ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8" }}>
                      {tagSearch ? `No tags match "${tagSearch}"` : "No tags available"}
                    </Typography>
                  </Box>
                ) : (
                  tags
                    .filter((t) => t.name.toLowerCase().includes(tagSearch.toLowerCase()))
                    .map((tag) => {
                      const isSelected = pendingTagIds.includes(tag.id);
                      return (
                        <MenuItem
                          key={tag.id}
                          value={tag.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingTagIds((prev) =>
                              prev.includes(tag.id)
                                ? prev.filter((id) => id !== tag.id)
                                : [...prev, tag.id]
                            );
                          }}
                          sx={{
                            py: 1, px: 2, mx: 0.75, borderRadius: "10px", mb: 0.25,
                            transition: "all 0.15s ease",
                            "&:hover": { backgroundColor: mode === "dark" ? "rgba(30,139,175,0.12)" : "rgba(30,139,175,0.06)" },
                            "&.Mui-selected": { backgroundColor: mode === "dark" ? "rgba(30,139,175,0.18)" : "rgba(30,139,175,0.08)", "&:hover": { backgroundColor: mode === "dark" ? "rgba(30,139,175,0.22)" : "rgba(30,139,175,0.11)" } },
                          }}
                        >
                          <Box sx={{
                            width: 18, height: 18, borderRadius: "5px",
                            border: pendingTagIds.includes(tag.id) ? "2px solid #1e8baf" : mode === "dark" ? "2px solid rgba(255,255,255,0.2)" : "2px solid rgba(30,58,138,0.2)",
                            background: pendingTagIds.includes(tag.id) ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center", mr: 1.5, flexShrink: 0, transition: "all 0.15s ease", pointerEvents: "none"
                          }}>
                            {pendingTagIds.includes(tag.id) && (
                              <Box component="svg" viewBox="0 0 12 12" sx={{ width: 10, height: 10 }}>
                                <polyline points="2,6 5,9 10,3" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </Box>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: pendingTagIds.includes(tag.id) ? 600 : 400, color: mode === "dark" ? "#e0e0e0" : pendingTagIds.includes(tag.id) ? "#1e3a8a" : "#334155", flex: 1 }}>
                            {tag.name}
                          </Typography>
                          {pendingTagIds.includes(tag.id) && (
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "linear-gradient(135deg, #1e3a8a, #1e8baf)", ml: 1, flexShrink: 0, pointerEvents: "none" }} />
                          )}
                        </MenuItem>
                      );
                    })
                )}
              </Box>

              <Box
                sx={{
                  px: 2, py: 1.5,
                  borderTop: mode === "dark" ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(30,58,138,0.08)",
                  background: mode === "dark" ? "#1e1b18" : "#ffffff",
                  position: "sticky", bottom: 0, zIndex: 10,
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Button size="small"
                    onClick={(e) => { e.stopPropagation(); setPendingTagIds(tags.map((t) => t.id)); }}
                    disabled={pendingTagIds.length === tags.length || tags.length === 0}
                    sx={{
                      textTransform: "none", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
                      px: 1.5, py: 0.5, color: "#1e8baf", border: "1px solid rgba(30,139,175,0.3)",
                      "&:hover": { borderColor: "#1e8baf", backgroundColor: "rgba(30,139,175,0.06)" },
                      "&.Mui-disabled": { opacity: 0.4 }
                    }}
                  >
                    All
                  </Button>
                  <Button size="small"
                    onClick={(e) => { e.stopPropagation(); setPendingTagIds([]); }}
                    disabled={pendingTagIds.length === 0}
                    sx={{
                      textTransform: "none", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
                      px: 1.5, py: 0.5, color: "#64748b", border: "1px solid rgba(100,116,139,0.25)",
                      "&:hover": { borderColor: "#94a3b8", backgroundColor: "rgba(100,116,139,0.06)" },
                      "&.Mui-disabled": { opacity: 0.4 }
                    }}
                  >
                    Clear
                  </Button>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {pendingTagIds.length > 0 && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#1e8baf", fontWeight: 600 }}>
                      {pendingTagIds.length} selected
                    </Typography>
                  )}
                  <Button size="small" variant="contained"
                    onClick={(e) => { e.stopPropagation(); setTagIds(pendingTagIds); setTagDropdownOpen(false); }}
                    sx={{
                      textTransform: "none", borderRadius: "8px", fontSize: "0.78rem", fontWeight: 700,
                      px: 2.5, py: 0.65,
                      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                      boxShadow: "0 2px 8px rgba(30,58,138,0.25)",
                      "&:hover": { background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)", boxShadow: "0 4px 12px rgba(30,58,138,0.35)" }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Select>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel sx={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1e3a8a", opacity: 0.75, mb: 1.5, "&.Mui-focused": { color: "#1e8baf" } }}>
              Question Types
            </FormLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {["FIB", "TF", "MCQ", "IMAGE", "ALL"].map((type) => (
                <Box
                  key={type} onClick={() => handleCheckboxChange(type)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "6px", px: "16px", py: "7px", borderRadius: "50px",
                    border: `1.5px solid ${selectedOptions[type] ? "#1e8baf" : "rgba(30,58,138,0.15)"}`,
                    background: selectedOptions[type] ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)" : mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.8)",
                    cursor: "pointer", userSelect: "none", transition: "all 0.2s ease",
                    fontWeight: selectedOptions[type] ? 600 : 400, fontSize: "0.82rem",
                    color: selectedOptions[type] ? "#fff" : mode === "dark" ? "#90caf9" : mode === "custom" ? customColors.text : "#64748b",
                    "&:hover": { borderColor: "#1e8baf", background: "rgba(30,139,175,0.06)", transform: "translateY(-1px)" },
                  }}
                >
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: selectedOptions[type] ? "#fff" : "rgba(30,58,138,0.2)", transition: "background 0.2s" }} />
                  {type}
                </Box>
              ))}
            </Box>
          </FormControl>

          {Object.keys(tagQuestions).length > 0 && (
            <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
              <FormLabel sx={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1e3a8a", opacity: 0.75, mb: 1.5 }}>
                Select Questions ({selectedQuestions.length} selected)
              </FormLabel>
              {isQuestionsLoading ? (
                <CircularProgress size={24} />
              ) : (
                Object.entries(tagQuestions).map(([type, questions]) => {
                  const isExpanded = expandedTypes[type] !== false;
                  return (
                    <Box key={type} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "8px",
                          bgcolor: "rgba(30,58,138,0.06)",
                          mb: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }))}
                      >
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#1e3a8a", letterSpacing: "0.06em" }}>
                          {type} ({questions.length})
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Button
                            size="small"
                            sx={{ fontSize: "0.7rem", textTransform: "none", color: "#1e8baf", p: "2px 8px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const typeQIds = questions.map((q) => q.id);
                              const allSelected = typeQIds.every((id) => selectedQuestions.some((sq) => sq.id === id));
                              if (allSelected) {
                                setSelectedQuestions((prev) => prev.filter((sq) => !typeQIds.includes(sq.id)));
                              } else {
                                const toAdd = questions
                                  .filter((q) => !selectedQuestions.some((sq) => sq.id === q.id))
                                  .map((q) => ({ type: q.question_type, id: q.id, folder_id: q.subfolder_id || q.folder || q.folder_id }));
                                setSelectedQuestions((prev) => [...prev, ...toAdd]);
                              }
                            }}
                          >
                            {questions.every((q) => selectedQuestions.some((sq) => sq.id === q.id)) ? "Deselect All" : "Select All"}
                          </Button>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] })); }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </Box>
                      {isExpanded && (
                        <>
                          {questions.map((q) => {
                            const isChecked = selectedQuestions.some((sq) => sq.id === q.id);
                            return (
                              <Box
                                key={q.id}
                                onClick={() =>
                                  setSelectedQuestions((prev) =>
                                    isChecked
                                      ? prev.filter((sq) => sq.id !== q.id)
                                      : [...prev, { type: q.question_type, id: q.id, folder_id: q.subfolder_id || q.folder || q.folder_id }]
                                  )
                                }
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  mb: 0.5,
                                  border: `1.5px solid ${isChecked ? "#1e8baf" : "rgba(30,58,138,0.1)"}`,
                                  bgcolor: isChecked ? "rgba(30,139,175,0.06)" : "rgba(255,255,255,0.7)",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    borderColor: "#1e8baf",
                                    bgcolor: "rgba(30,139,175,0.04)",
                                  },
                                }}
                              >
                                <Checkbox
                                  checked={isChecked}
                                  size="small"
                                  sx={{
                                    p: 0,
                                    mt: "2px",
                                    color: "rgba(30,58,138,0.3)",
                                    "&.Mui-checked": { color: "#1e8baf" },
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.83rem",
                                      color: "#334155",
                                      lineHeight: 1.5,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {renderContent(q.statement)}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                                    {q.tags?.map((tag) => (
                                      <Chip
                                        key={tag.id}
                                        label={tag.name}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: "0.65rem",
                                          bgcolor: "rgba(30,58,138,0.08)",
                                          color: "#1e3a8a",
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                                {q.question_type === "IMAGE" && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImageQuestion(q);
                                      setImageModalOpen(true);
                                    }}
                                    sx={{
                                      mt: 1,
                                      textTransform: "none",
                                      borderRadius: "8px",
                                      px: 2,
                                      borderColor: "#1e8baf",
                                      color: "#1e8baf",
                                      "&:hover": {
                                        borderColor: "#1e8baf",
                                        backgroundColor: "rgba(30, 139, 175, 0.08)",
                                      },
                                    }}
                                  >
                                    Preview Image
                                  </Button>
                                )}
                              </Box>
                            );
                          })}
                        </>
                      )}
                    </Box>
                  );
                })
              )}
            </FormControl>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label="Number of Questions"
              type="number"
              value={numberOfQuestions !== null ? numberOfQuestions : ""}
              onChange={(e) => {
                const val = e.target.value;
                const max = getTotalAvailableQuestions();
                if (val === "") {
                  setNumberOfQuestions(null);
                } else if (Number(val) >= 1 && Number(val) <= max) {
                  setNumberOfQuestions(Number(val));
                }
              }}
              onBlur={() => {
                const max = getTotalAvailableQuestions();
                if (numberOfQuestions === null || numberOfQuestions === "") {
                  setNumberOfQuestions(null);
                } else if (numberOfQuestions < 1) {
                  setNumberOfQuestions(1);
                } else if (numberOfQuestions > max) {
                  setNumberOfQuestions(max);
                }
              }}
              inputProps={{ min: 1, max: getTotalAvailableQuestions() }}
              helperText={
                selectedQuestions.length > 0
                  ? "Auto-filled based on selected questions"
                  : `${getTotalAvailableQuestions()} questions are available`
              }
              error={numberOfQuestions !== null && numberOfQuestions > getTotalAvailableQuestions()}
              disabled={selectedQuestions.length > 0}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                  "& fieldset": { borderColor: "rgba(30,58,138,0.15)" },
                  "&:hover fieldset": { borderColor: "rgba(30,139,175,0.4)" },
                  "&.Mui-focused fieldset": { borderColor: "#1e8baf", borderWidth: "1.5px" },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#1e8baf" },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.72rem",
                  color: numberOfQuestions !== null && numberOfQuestions > getTotalAvailableQuestions() ? "#d32f2f" : "#94a3b8",
                },
              }}
            />
            <TextField
              fullWidth label="Max Attempts" type="number"
              value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)}
              placeholder="Leave empty for unlimited"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                  "& fieldset": { borderColor: "rgba(30,58,138,0.15)" },
                  "&:hover fieldset": { borderColor: "rgba(30,139,175,0.4)" },
                  "&.Mui-focused fieldset": { borderColor: "#1e8baf", borderWidth: "1.5px" }
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#1e8baf" }
              }}
            />
            <TextField
              fullWidth label="Passing Percentage" type="number"
              value={passingPercentage} onChange={(e) => setPassingPercentage(e.target.value)}
              placeholder="Leave empty for 35%"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                  "& fieldset": { borderColor: "rgba(30,58,138,0.15)" },
                  "&:hover fieldset": { borderColor: "rgba(30,139,175,0.4)" },
                  "&.Mui-focused fieldset": { borderColor: "#1e8baf", borderWidth: "1.5px" }
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#1e8baf" }
              }}
            />
          </Box>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel sx={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1e3a8a", opacity: 0.75, mb: 1.5, "&.Mui-focused": { color: "#1e8baf" } }}>
              Negative Marking
            </FormLabel>
            <RadioGroup row value={negativeMarking ? "yes" : "no"} onChange={(e) => setNegativeMarking(e.target.value === "yes")} sx={{ gap: 1.5 }}>
              {["yes", "no"].map((val) => (
                <FormControlLabel
                  key={val} value={val}
                  control={<Radio sx={{ display: "none" }} />}
                  label={val.charAt(0).toUpperCase() + val.slice(1)}
                  sx={{
                    m: 0, px: "18px", py: "8px", borderRadius: "50px",
                    border: "1.5px solid rgba(30,58,138,0.15)", background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.9)",
                    transition: "all 0.2s ease", cursor: "pointer",
                    "& .MuiFormControlLabel-label": { fontSize: "0.85rem", color: "#64748b" },
                    "&:has(.Mui-checked)": {
                      borderColor: "#1e8baf",
                      background: "linear-gradient(135deg, rgba(30,139,175,0.12) 0%, rgba(30,58,138,0.08) 100%)",
                      "& .MuiFormControlLabel-label": { fontWeight: 600, color: "#1e3a8a" }
                    },
                    "&:hover": { borderColor: "#1e8baf", background: "rgba(30,139,175,0.06)" },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, mb: 3, borderRadius: "12px", border: "1.5px solid rgba(30,58,138,0.1)", background: mode === "dark" ? "#2a2725" : mode === "custom" ? customColors.bg : "rgba(255,255,255,0.7)" }}>
            <Typography sx={{ fontSize: "0.88rem", color: mode === "dark" ? "#e0e0e0" : mode === "custom" ? customColors.text : "#334155", fontWeight: 500 }}>Enable Timer</Typography>
            <Switch
              checked={isTimerEnabled}
              onChange={(e) => setIsTimerEnabled(e.target.checked)}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#1e8baf", "& + .MuiSwitch-track": { background: "#1e8baf" } } }}
            />
          </Box>

          {isTimerEnabled && (
            <TextField
              fullWidth label="Time Limit (minutes)" type="number"
              value={timeLimitMinutes} onChange={(e) => setTimeLimitMinutes(e.target.value)}
              placeholder="e.g. 60"
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: "rgba(255,255,255,0.9)",
                  "& fieldset": { borderColor: "rgba(30,58,138,0.15)" },
                  "&:hover fieldset": { borderColor: "rgba(30,139,175,0.4)" },
                  "&.Mui-focused fieldset": { borderColor: "#1e8baf", borderWidth: "1.5px" }
                },
                "& .MuiInputLabel-root.Mui-focused": { color: "#1e8baf" },
              }}
            />
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleStartQuiz}
              disabled={!quizTitle.trim() || selectedFolders.length === 0 || isLoading}
              sx={{
                py: 1.7, borderRadius: "14px", textTransform: "none", fontWeight: 700,
                fontSize: "0.95rem", letterSpacing: "0.05em",
                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                boxShadow: "0 4px 20px rgba(30,58,138,0.25)", transition: "all 0.3s ease",
                "&:hover": { background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)", boxShadow: "0 6px 24px rgba(30,58,138,0.35)", transform: "translateY(-2px)" },
                "&:active": { transform: "translateY(0)" },
                "&.Mui-disabled": {
                  background: mode === "dark" ? "rgba(255,255,255,0.08)" : mode === "custom" ? customColors.bg : "rgba(30,58,138,0.08)",
                  color: mode === "dark" ? "rgba(255,255,255,0.3)" : mode === "custom" ? customColors.text : "rgba(30,58,138,0.3)",
                  boxShadow: "none",
                  transform: "none",
                }
              }}
            >
              {isLoading ? "Starting Quiz..." : "Create Quiz"}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Modal
        open={quizCreatedModalOpen}
        onClose={() => setQuizCreatedModalOpen(false)}
        aria-labelledby="quiz-created-modal-title"
        aria-describedby="quiz-created-modal-description"
      >
        <Fade in={quizCreatedModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 450,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}>
            <Typography id="quiz-created-modal-title" variant="h6" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
              Quiz Created Successfully!
            </Typography>
            <Typography id="quiz-created-modal-description" sx={{ mb: 3 }}>
              Your quiz has been created. What would you like to do next?
            </Typography>
           <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
  {!isStudent && (
    <Button
      variant="outlined"
      onClick={handleScheduleQuiz}
      sx={{ color: '#4caf50', borderColor: '#4caf50' }}
    >
      Schedule
    </Button>
  )}
  <Button
    variant="contained"
    onClick={handleStartNow}
    sx={{
      background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
      "&:hover": { background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)" }
    }}
  >
    Start Now
  </Button>
</Box>
          </Box>
        </Fade>
      </Modal>

      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Fade in={imageModalOpen}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90%",
              maxHeight: "90vh",
              overflow: "auto",
              bgcolor: "background.paper",
              borderRadius: "16px",
              boxShadow: 24,
              p: 3,
            }}
          >
            <IconButton
              onClick={() => setImageModalOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  transform: "rotate(90deg)",
                },
                transition: "all 0.3s ease",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CloseIcon sx={{ color: "#2c3e50" }} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                textAlign: "center",
                color: mode === "dark" ? "#ffffff" : "#2c3e50",
              }}
            >
              {selectedImageQuestion?.statement}
            </Typography>
            <Box sx={{ textAlign: "center" }}>
              <canvas
                ref={(node) => {
                  if (node && selectedImageQuestion) {
                    const ctx = node.getContext("2d");
                    const img = new window.Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => {
                      const maxWidth = 800;
                      const aspectRatio = img.width / img.height;
                      const canvasWidth = Math.min(maxWidth, img.width);
                      const canvasHeight = canvasWidth / aspectRatio;

                      node.width = canvasWidth;
                      node.height = canvasHeight;
                      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

                      const colors = [
                        'rgba(255, 0, 0, 0.6)', 'rgba(0, 0, 255, 0.6)',
                        'rgba(0, 255, 0, 0.6)', 'rgba(255, 255, 0, 0.6)',
                        'rgba(255, 0, 255, 0.6)', 'rgba(0, 255, 255, 0.6)',
                        'rgba(255, 128, 0, 0.6)', 'rgba(128, 0, 255, 0.6)',
                      ];
                      (selectedImageQuestion.labels || []).forEach((label, index) => {
                        ctx.fillStyle = colors[index % colors.length];
                        ctx.fillRect(label.x, label.y, label.width, label.height);
                        ctx.fillStyle = '#ffffff';
                        ctx.font = 'bold 24px Arial';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(label.id, label.x + label.width / 2, label.y + label.height / 2);
                      });
                    };
                    img.src = selectedImageQuestion.file_url.startsWith("http")
                      ? selectedImageQuestion.file_url
                      : `http://warals1.ddns.net:9017${selectedImageQuestion.file_url}`;
                  }
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                }}
              />
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateQuiz;
